import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { RefreshCw, Upload, Clock, CheckCircle2, AlertCircle, FileText, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function A3Sync() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/sync/status`),
        axios.get(`${API}/admin/sync/logs?limit=15`),
      ]);
      setStatus(sRes.data);
      setLogs(lRes.data || []);
    } catch {
      toast.error("Error cargando estado de sincronización");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sync/run`);
      toast.success(`Sync completado: ${res.data.totals?.upserted || 0} actualizados, ${res.data.totals?.errors || 0} errores`);
      fetchData();
    } catch {
      toast.error("Error ejecutando sincronización");
    } finally {
      setSyncing(false);
    }
  };

  const handleUpload = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(fileType);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/sync/upload?file_type=${fileType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error subiendo archivo");
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const FILE_TYPES = [
    { key: "branch_madrid", label: "Tarifas MADRID", expected: "branch_prices_madrid.csv" },
    { key: "branch_guadalajara", label: "Tarifas GUADALAJARA", expected: "branch_prices_guadalajara.csv" },
    { key: "special", label: "Precios Especiales", expected: "special_prices.csv" },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="a3-sync-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sincronización A3 ERP</h1>
              <p className="text-slate-500">Importación automática de precios (04:00 diaria)</p>
            </div>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            onClick={handleSync}
            disabled={syncing}
            data-testid="run-sync-btn"
          >
            <Play className="w-4 h-4" />
            {syncing ? "Sincronizando..." : "Ejecutar ahora"}
          </Button>
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Clock className="w-4 h-4" /> Próxima ejecución
              </div>
              <p className="font-semibold text-slate-900">
                {status?.next_scheduled_run ? formatDate(status.next_scheduled_run) : "No programada"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                {status?.last_sync?.status === "completed" ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                )}
                Última sincronización
              </div>
              <p className="font-semibold text-slate-900">
                {status?.last_sync ? formatDate(status.last_sync.started_at) : "Nunca"}
              </p>
              {status?.last_sync && (
                <p className="text-xs text-slate-500 mt-1">
                  {status.last_sync.totals?.upserted || 0} actualizados · {status.last_sync.totals?.errors || 0} errores
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <FileText className="w-4 h-4" /> Archivos CSV
              </div>
              <p className="font-semibold text-slate-900">
                {status?.csv_files?.length || 0} archivos
              </p>
              <p className="text-xs text-slate-500">{status?.sync_dir}</p>
            </CardContent>
          </Card>
        </div>

        {/* CSV upload section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Archivos CSV de A3</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Sube los CSV exportados desde A3 ERP. Los archivos se procesarán en la próxima sincronización o al pulsar "Ejecutar ahora".
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {FILE_TYPES.map((ft) => {
                const existing = status?.csv_files?.find((f) => f.name === ft.expected);
                return (
                  <div key={ft.key} className="border rounded-lg p-4">
                    <p className="font-medium text-sm text-slate-900 mb-1">{ft.label}</p>
                    {existing ? (
                      <p className="text-xs text-green-600 mb-2">
                        {existing.name} ({existing.size_kb} KB) · {formatDate(existing.modified)}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mb-2">Sin archivo</p>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => handleUpload(e, ft.key)}
                        data-testid={`upload-${ft.key}`}
                      />
                      <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                        uploading === ft.key
                          ? "bg-slate-200 text-slate-500"
                          : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                      }`}>
                        <Upload className="w-3 h-3" />
                        {uploading === ft.key ? "Subiendo..." : existing ? "Reemplazar" : "Subir CSV"}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
              <strong>Formato CSV esperado:</strong><br />
              Tarifas sede: <code>erp_code,price</code><br />
              Precios especiales: <code>erp_customer_code,erp_code,price</code>
            </div>
          </CardContent>
        </Card>

        {/* Sync logs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Historial de sincronizaciones</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No hay registros de sincronización</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Procesados</TableHead>
                      <TableHead className="text-right">Actualizados</TableHead>
                      <TableHead className="text-right">Errores</TableHead>
                      <TableHead>Duración</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, i) => (
                      <TableRow
                        key={log.sync_id || i}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedLog(expandedLog === i ? null : i)}
                        data-testid={`sync-log-${i}`}
                      >
                        <TableCell className="text-sm">{formatDate(log.started_at)}</TableCell>
                        <TableCell className="text-sm">{log.triggered_by}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            log.status === "completed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {log.status === "completed" ? "OK" : "Error"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{log.totals?.processed || 0}</TableCell>
                        <TableCell className="text-right font-medium">{log.totals?.upserted || 0}</TableCell>
                        <TableCell className="text-right">
                          {(log.totals?.errors || 0) > 0 ? (
                            <span className="text-red-600 font-medium">{log.totals.errors}</span>
                          ) : "0"}
                        </TableCell>
                        <TableCell className="text-sm">{log.duration_seconds ? `${log.duration_seconds.toFixed(1)}s` : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {expandedLog !== null && logs[expandedLog]?.files?.length > 0 && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-sm mb-2">Detalle archivos procesados:</h4>
                {logs[expandedLog].files.map((f, fi) => (
                  <div key={fi} className="text-xs mb-2 p-2 bg-white rounded border">
                    <span className="font-mono font-medium">{f.file}</span>
                    {f.branch && <span className="ml-2 text-indigo-600">[{f.branch}]</span>}
                    <span className="ml-2">{f.upserted}/{f.processed} OK</span>
                    {f.errors > 0 && <span className="ml-2 text-red-600">{f.errors} errores</span>}
                    {f.error_details?.length > 0 && (
                      <ul className="mt-1 ml-4 text-red-500 list-disc">
                        {f.error_details.slice(0, 5).map((d, di) => <li key={di}>{d}</li>)}
                        {f.error_details.length > 5 && <li>...y {f.error_details.length - 5} más</li>}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
